import React from 'react'
import { Receipt } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { QuantityInput } from '@/components/ui/QuantityInput'
import { SupplierManager } from './SupplierManager'
import { MaterialFormData } from './types'
import { useTemplateForm } from '@/hooks/useTemplateForm'

interface PurchaseSectionProps {
  formData: MaterialFormData
  onInputChange: (field: keyof MaterialFormData, value: string | number | boolean) => void
  errors: Record<string, string>
  conversionLoading: boolean
  getUnitCategory: (unitType: string) => string
  getRecommendedUnits: (category: string) => string[]
}

export const PurchaseSection: React.FC<PurchaseSectionProps> = ({
  formData,
  onInputChange,
  errors,
  conversionLoading,
  getUnitCategory,
  getRecommendedUnits
}) => {
  // Get current template information
  const { currentTemplate } = useTemplateForm()

  // Template-aware helper function
  const getTemplatePurchaseTitle = (): string => {
    if (!currentTemplate) return 'Purchase Information'
    
    switch (currentTemplate.businessType) {
      case '3d_printing':
        return 'Filament/Material Purchase Information'
      case 'jewelry':
        return 'Jewelry Supply Purchase Information'
      case 'woodworking':
        return 'Wood/Hardware Purchase Information'
      case 'electronics':
        return 'Component Purchase Information'
      case 'automotive':
        return 'Parts Purchase Information'
      default:
        return `${currentTemplate.name} Purchase Information`
    }
  }

  // Helper functions for unit-specific text and placeholders
  const getUnitTypeLabel = (unit: string): string => {
    const labels: Record<string, string> = {
      'package': 'Package',
      'set': 'Set',
      'dozen': 'Dozen',
      'gross': 'Gross',
      'strand': 'Strand',
      'pair': 'Pair'
    }
    return labels[unit] || 'Package'
  }

  const getUnitExplanation = (unit: string): string => {
    const explanations: Record<string, string> = {
      'package': 'Specify how many packages you bought and how many pieces are in each package.',
      'set': 'Specify how many sets you bought and how many pieces are in each set.',
      'dozen': 'Specify how many dozens you bought (each dozen contains 12 pieces).',
      'gross': 'Specify how many gross you bought (each gross contains 144 pieces).',
      'strand': 'Specify how many strands you bought and how many beads are on each strand.',
      'pair': 'Specify how many pairs you bought (each pair contains 2 pieces).'
    }
    return explanations[unit] || 'Specify the batch details below.'
  }

  const getUnitPluralName = (unit: string): string => {
    const plurals: Record<string, string> = {
      'package': 'Packages',
      'set': 'Sets',
      'dozen': 'Dozens',
      'gross': 'Gross',
      'strand': 'Strands',
      'pair': 'Pairs'
    }
    return plurals[unit] || 'Packages'
  }

  const getQuantityPlaceholder = (unit: string): string => {
    const placeholders: Record<string, string> = {
      'package': 'e.g., 5',
      'set': 'e.g., 3',
      'dozen': 'e.g., 2',
      'gross': 'e.g., 1',
      'strand': 'e.g., 10',
      'pair': 'e.g., 25'
    }
    return placeholders[unit] || 'e.g., 5'
  }

  const getQuantityHelpText = (unit: string): string => {
    const helpTexts: Record<string, string> = {
      'package': 'How many packages did you buy?',
      'set': 'How many sets did you buy?',
      'dozen': 'How many dozens did you buy?',
      'gross': 'How many gross did you buy?',
      'strand': 'How many strands did you buy?',
      'pair': 'How many pairs did you buy?'
    }
    return helpTexts[unit] || 'How many did you buy?'
  }

  const getItemsPlaceholder = (unit: string): string => {
    const placeholders: Record<string, string> = {
      'package': 'e.g., 100',
      'set': 'e.g., 6',
      'dozen': '12',
      'gross': '144',
      'strand': 'e.g., 50',
      'pair': '2'
    }
    return placeholders[unit] || 'e.g., 100'
  }

  const getItemsHelpText = (unit: string): string => {
    const helpTexts: Record<string, string> = {
      'package': 'How many pieces in each package?',
      'set': 'How many pieces in each set?',
      'dozen': 'Fixed at 12 pieces per dozen',
      'gross': 'Fixed at 144 pieces per gross',
      'strand': 'How many beads on each strand?',
      'pair': 'Fixed at 2 pieces per pair'
    }
    return helpTexts[unit] || 'How many pieces in each unit?'
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-green-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {getTemplatePurchaseTitle()}
          </h2>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.includePurchase}
            onChange={(e) => onInputChange('includePurchase', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Include initial purchase
          </span>
        </label>
      </div>

      {formData.includePurchase && (
        <div className="mt-6 space-y-6">
          {/* Package Purchase vs Regular Purchase */}
          {formData.isPackagePurchase ? (
            /* Package Purchase Section */
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Package Purchase Information
              </h3>
              <div className="bg-purple-100 dark:bg-purple-800/30 p-3 rounded-md mb-4">
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  <strong>{getUnitTypeLabel(formData.baseUnit)} Purchase Detected:</strong> Since you selected "{formData.baseUnit}" as the base unit, 
                  this material requires batch/package purchasing information. {getUnitExplanation(formData.baseUnit)}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Number of {getUnitPluralName(formData.baseUnit)} *
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.packageQuantity}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onInputChange('packageQuantity', parseInt(e.target.value) || 0)
                    }
                    placeholder={getQuantityPlaceholder(formData.baseUnit)}
                    className={errors.packageQuantity ? 'border-red-500' : ''}
                  />
                  {errors.packageQuantity && (
                    <p className="text-red-500 text-sm mt-1">{errors.packageQuantity}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{getQuantityHelpText(formData.baseUnit)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Items per {formData.baseUnit} *
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.itemsPerPackage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onInputChange('itemsPerPackage', parseInt(e.target.value) || 0)
                    }
                    placeholder={getItemsPlaceholder(formData.baseUnit)}
                    className={errors.itemsPerPackage ? 'border-red-500' : ''}
                    readOnly={formData.baseUnit === 'dozen' || formData.baseUnit === 'gross' || formData.baseUnit === 'pair'}
                  />
                  {errors.itemsPerPackage && (
                    <p className="text-red-500 text-sm mt-1">{errors.itemsPerPackage}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{getItemsHelpText(formData.baseUnit)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cost per {formData.baseUnit} *
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.packageCost}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onInputChange('packageCost', parseFloat(e.target.value) || 0)
                    }
                    placeholder="0.00"
                    className={errors.packageCost ? 'border-red-500' : ''}
                  />
                  {errors.packageCost && (
                    <p className="text-red-500 text-sm mt-1">{errors.packageCost}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Price per {formData.baseUnit}</p>
                </div>
              </div>

              {/* Package Calculation Summary */}
              {formData.packageQuantity > 0 && formData.itemsPerPackage > 0 && formData.packageCost > 0 && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                  <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">
                    Package Calculation
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-green-900 dark:text-green-100">{getUnitPluralName(formData.baseUnit)}:</span>
                      <span className="font-bold text-green-900 dark:text-green-100">{formData.packageQuantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-green-900 dark:text-green-100">Total Items:</span>
                      <span className="font-bold text-green-900 dark:text-green-100">
                        {formData.packageQuantity * formData.itemsPerPackage}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-green-900 dark:text-green-100">Total Cost:</span>
                      <span className="font-bold text-green-900 dark:text-green-100">
                        ${(formData.packageQuantity * formData.packageCost).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-green-900 dark:text-green-100">Cost per Item:</span>
                      <span className="font-bold text-green-900 dark:text-green-100">
                        ${(formData.packageCost / formData.itemsPerPackage).toFixed(4)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Regular Purchase Section */
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Purchase Quantity
              </h3>
              
              <QuantityInput
                value={formData.purchaseQuantity}
                unit={formData.purchaseUnit}
                onValueChange={(value) => onInputChange('purchaseQuantity', value)}
                onUnitChange={(unit) => onInputChange('purchaseUnit', unit)}
                baseUnit={formData.baseUnit}
                category={getUnitCategory(formData.baseUnit)}
                recommendedUnits={getRecommendedUnits(getUnitCategory(formData.baseUnit))}
                label="Purchase Quantity *"
                placeholder="Enter quantity purchased..."
                required
                showConversion={formData.purchaseUnit !== formData.baseUnit}
                className={errors.purchaseQuantity ? 'border-red-500' : ''}
              />
              {errors.purchaseQuantity && (
                <p className="text-red-500 text-sm mt-1">{errors.purchaseQuantity}</p>
              )}
              {errors.purchaseUnit && (
                <p className="text-red-500 text-sm mt-1">{errors.purchaseUnit}</p>
              )}

              {/* Show conversion result */}
              {formData.purchaseQuantity > 0 && formData.convertedQuantity > 0 && formData.purchaseUnit !== formData.baseUnit && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                  <div className="text-sm text-green-800 dark:text-green-200">
                    <strong>Inventory Stock:</strong> {formData.convertedQuantity.toFixed(4)} {formData.baseUnit}
                    {conversionLoading && <span className="ml-2 text-gray-500">(Converting...)</span>}
                  </div>
                </div>
              )}

              {/* Show same unit message */}
              {formData.purchaseQuantity > 0 && formData.purchaseUnit === formData.baseUnit && (
                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Inventory Stock:</strong> {formData.purchaseQuantity} {formData.baseUnit} (same unit)
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cost Information - Only show for non-package purchases */}
          {!formData.isPackagePurchase && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Cost Information
                </h3>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="costInputType"
                      value="per-unit"
                      checked={formData.costInputType === 'per-unit'}
                      onChange={(e) => onInputChange('costInputType', e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Cost per {formData.baseUnit || 'unit'}
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="costInputType"
                      value="total"
                      checked={formData.costInputType === 'total'}
                      onChange={(e) => onInputChange('costInputType', e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Total cost
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.costInputType === 'per-unit' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Cost Per {formData.baseUnit || 'Unit'} *
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.costPerUnit}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onInputChange('costPerUnit', parseFloat(e.target.value) || 0)
                        }
                        placeholder="0.00"
                        className={errors.costPerUnit ? 'border-red-500' : ''}
                      />
                      {errors.costPerUnit && (
                        <p className="text-red-500 text-sm mt-1">{errors.costPerUnit}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Price per {formData.baseUnit || 'unit'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Total Purchase Cost
                      </label>
                      <Input
                        type="number"
                        value={formData.totalCost.toFixed(2)}
                        readOnly
                        className="bg-gray-100 dark:bg-gray-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">Automatically calculated</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Total Purchase Cost *
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.totalCost}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onInputChange('totalCost', parseFloat(e.target.value) || 0)
                        }
                        placeholder="0.00"
                        className={errors.totalCost ? 'border-red-500' : ''}
                      />
                      {errors.totalCost && (
                        <p className="text-red-500 text-sm mt-1">{errors.totalCost}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Total amount paid</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Cost Per {formData.baseUnit || 'Unit'}
                      </label>
                      <Input
                        type="number"
                        value={formData.costPerUnit.toFixed(4)}
                        readOnly
                        className="bg-gray-100 dark:bg-gray-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">For project costing</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Purchase Details */}
          <div className="space-y-6">
            {/* Supplier Management */}
            <SupplierManager
              selectedSupplierId={formData.supplierId}
              onSupplierChange={(supplierId) => onInputChange('supplierId', supplierId)}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Purchase Date *
              </label>
              <Input
                type="date"
                value={formData.purchaseDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  onInputChange('purchaseDate', e.target.value)
                }
                className={errors.purchaseDate ? 'border-red-500' : ''}
              />
                {errors.purchaseDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.purchaseDate}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Invoice Number (Optional)
                </label>
                <Input
                  type="text"
                  value={formData.invoiceNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onInputChange('invoiceNumber', e.target.value)
                  }
                  placeholder="e.g., INV-2024-001"
                />
              </div>
            </div>
          </div>

          {/* Cost Summary - Only show for non-package purchases */}
          {!formData.isPackagePurchase && formData.convertedQuantity > 0 && formData.totalCost > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
                Purchase Summary
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    Purchased:
                  </span>
                  <span className="font-bold text-blue-900 dark:text-blue-100">
                    {formData.purchaseQuantity} {formData.purchaseUnit}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    Inventory Stock:
                  </span>
                  <span className="font-bold text-blue-900 dark:text-blue-100">
                    {formData.convertedQuantity.toFixed(4)} {formData.baseUnit}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    Total Cost:
                  </span>
                  <span className="font-bold text-blue-900 dark:text-blue-100">
                    ${formData.totalCost.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    Cost per {formData.baseUnit}:
                  </span>
                  <span className="font-bold text-blue-900 dark:text-blue-100">
                    ${formData.costPerUnit.toFixed(4)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Purchase Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                onInputChange('notes', e.target.value)
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Additional notes about this purchase..."
            />
          </div>
        </div>
      )}
    </Card>
  )
}